import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import {
  Arbitrum,
  Base,
  Chain,
  Ethereum,
  Mode,
  Optimism,
  Scroll
} from '@thirdweb-dev/chains';
import { getContract, useAddress } from '@thirdweb-dev/react';
import nftMarketplaceAbi from 'abis/NFTMarketPlace.json';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import { useConfig } from '@app/config';
import { ApplicationConfig } from '@app/config/types';
import { BigNumber } from 'ethers';
import { useEffect } from 'react';
import { ModeChain } from '@app/constants/chains';
import { usePremiumBuyerNft } from '@app/hooks/nft/usePremiumBuyerNft';
import { useOwnedRewardsNfts } from '@app/hooks/nft/useOwnedRewardsNfts';

async function fetcher(
  network: Chain | undefined,
  address: string | undefined,
  config: ApplicationConfig | undefined
) {
  if (!network || !address || !config) {
    return null;
  }

  try {
    const contract = await getContract({
      address: config.CONTRACTS.NFT_MARKETPLACE,
      network,
      contractTypeOrAbi: nftMarketplaceAbi
    });

    // @ts-ignore
    const minAllocationForListing: BigNumber = await contract.call(
      'minXKimAllocationForStandardListings'
    );

    // @ts-ignore
    const minAllocationForPremiumListing: BigNumber = await contract.call(
      'minXKimAllocationForPremiumListings'
    );

    // @ts-ignore
    const totalAllocated: BigNumber = await contract.call(
      'totalUserXKimAllocationToPlugin',
      [address]
    );

    return {
      isEligibleForStandard: totalAllocated.gte(minAllocationForListing),
      isEligibleForPremium: totalAllocated.gte(minAllocationForPremiumListing)
    };
  } catch (e) {
    console.error(e);

    return null;
  }
}

export function useCheckUserMarketplaceEligibility() {
  const {
    chain: chainId,
    actions: {
      setIsEligibleToParticipate,
      setIsEligibleToParticipateInPremium,
      setOwnedPremiumNFTTokenId
    }
  } = useNftMarketplaceFiltersState();
  const config = useConfig();
  const address = useAddress();
  const chain = chainId ? getChainById(chainId) : undefined;
  const { address: premiumBuyerNftAddress } = usePremiumBuyerNft();
  const { data: ownedNfts } = useOwnedRewardsNfts(premiumBuyerNftAddress);

  const { data } = useQuery<{
    isEligibleForStandard: boolean;
    isEligibleForPremium: boolean;
  } | null>(
    [
      QueryKeys.CHECK_NFT_MARKETPLACE_USER_ELIGIBILITY,
      chain?.chainId,
      address,
      !!config,
      premiumBuyerNftAddress
    ],
    () => fetcher(chain, address, config),
    {
      enabled: Boolean(chain && config && address)
    }
  );

  useEffect(() => {
    setIsEligibleToParticipate(!!data?.isEligibleForStandard);
    setIsEligibleToParticipateInPremium(
      !!data?.isEligibleForPremium && !!ownedNfts?.length
    );
    setOwnedPremiumNFTTokenId(ownedNfts?.[0]?.metadata.id ?? '');
  }, [
    data,
    ownedNfts,
    ownedNfts?.length,
    setIsEligibleToParticipate,
    setIsEligibleToParticipateInPremium,
    setOwnedPremiumNFTTokenId
  ]);
}

function getChainById(chainId: number): Chain | undefined {
  return [Scroll, Base, Ethereum, Optimism, Arbitrum, ModeChain].find(
    c => c.chainId === chainId
  );
}
